import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export default function WhyInvest({ user, onLogout }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/learn')} 
          className="mb-6 dark:text-gray-300"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Learn Home
        </Button>

        <Card className="glass p-8 mb-6 dark:bg-gray-800">
          <div className="flex items-center space-x-2 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              Beginner
            </span>
            <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Clock className="w-4 h-4 mr-1" />
              8 min read
            </span>
          </div>

          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            💰 Why Invest? The Power of Wealth Building
          </h1>

          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Understand why investing is crucial for beating inflation and building long-term wealth
          </p>

          {/* Content */}
          <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
            
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">The Problem with Just Saving</h2>
              <p className="mb-4">
                Imagine you saved ₹1,00,000 in a savings account earning 4% interest. After a year, you'd have ₹1,04,000. 
                Sounds good? Not really. If inflation is 6%, the purchasing power of your money actually <strong>decreased</strong>!
              </p>
              
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border-l-4 border-red-500 my-4">
                <p className="text-sm">
                  <strong>⚠️ Reality Check:</strong> What cost ₹100 today will cost ₹106 next year (at 6% inflation). 
                  Your ₹104 (after 4% bank interest) can now buy less than before!
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">How Investing Helps</h2>
              <p className="mb-4">
                Investing in assets like stocks historically provides returns that beat inflation. Over the long term:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Stock Market (Nifty 50):</strong> ~12-15% average annual returns (last 20 years)</li>
                <li><strong>Real Estate:</strong> ~8-10% annual appreciation</li>
                <li><strong>Fixed Deposits:</strong> ~6-7% annual interest</li>
                <li><strong>Savings Account:</strong> ~3-4% annual interest</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Real Example: ₹10 Lakhs Over 20 Years</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border dark:border-gray-600 mb-4">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="p-3 text-left">Option</th>
                      <th className="p-3 text-right">Annual Return</th>
                      <th className="p-3 text-right">Value After 20 Years</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Savings Account (4%)</td>
                      <td className="p-3 text-right">4%</td>
                      <td className="p-3 text-right font-bold">₹21.9 L</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Fixed Deposit (7%)</td>
                      <td className="p-3 text-right">7%</td>
                      <td className="p-3 text-right font-bold">₹38.7 L</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/20">
                      <td className="p-3">Stock Market (12%)</td>
                      <td className="p-3 text-right">12%</td>
                      <td className="p-3 text-right font-bold text-green-600 dark:text-green-400">₹96.5 L</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-sm italic">
                Notice the difference? Investing in stocks (12% return) gives you <strong>4.4x more</strong> than savings accounts!
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">The 3 Key Reasons to Invest</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">1. Beat Inflation</h3>
                  <p className="text-sm">
                    Protect your purchasing power. If you earn 12% while inflation is 6%, you're actually gaining 6% in real terms.
                  </p>
                </div>

                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">2. Achieve Financial Goals</h3>
                  <p className="text-sm">
                    Whether it's buying a house, funding your child's education, or retiring comfortably - investing accelerates your goals.
                  </p>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">3. Create Passive Income</h3>
                  <p className="text-sm">
                    Dividends from stocks, rental income from property - your money works for you even while you sleep!
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-700">
              <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-3">💡 Key Takeaway</h3>
              <p className="mb-4">
                <strong>Investing isn't optional - it's essential.</strong> Keeping money idle in a savings account is actually 
                a guaranteed way to lose purchasing power due to inflation.
              </p>
              <p className="text-sm">
                Start early, stay consistent, and let compound interest do the heavy lifting. Even ₹5,000/month invested 
                at 12% for 20 years becomes ₹49.95 lakhs!
              </p>
            </div>

          </div>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/learn')}
            className="dark:bg-gray-800 dark:text-gray-100"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Lessons
          </Button>
          <Button 
            onClick={() => navigate('/learn/risk-return')}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
          >
            Next: Risk vs Return
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

      </div>
      <Footer />
    </div>
  );
}
